import { default as consignesAccident } from '@assets/pdf/consignes-accident.pdf';
import { default as demandeReconductionPdf } from '@assets/pdf/demande_reconduction36.pdf';
import { default as goldenRulesSimplifiedPdf } from '@assets/pdf/golden-rules-simplified.pdf';
import { MinimalStepperProps } from '@components';
import { useHomeSecurity } from '@contexts/HomeSecurityContext/HomeSecurityContext';
import { useGetPpspsByWorksiteIdMutation, GetPpspsByWorksiteIdResponse } from '@services';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export const useToolsPageVM = () => {
    const [currentStep, setCurrentStep] = useState<number>(0);
    const [isChecked, setIsChecked] = useState<boolean>(false);
    const [isPpspsModalOpen, setIsPpspsModalOpen] = useState<boolean>(false);
    const [ppsps, setPpsps] = useState<GetPpspsByWorksiteIdResponse | null>(null);
    const [steps, setSteps] = useState<{ label: string; status: string; pdf: string; hasSeenAllPages: boolean }[]>([
        {
            label: 'Le PPSPS',
            status: 'inProgress',
            pdf: demandeReconductionPdf,
            hasSeenAllPages: false
        },
        {
            label: "Les règles d'or",
            status: 'pending',
            pdf: goldenRulesSimplifiedPdf,
            hasSeenAllPages: false
        },
        {
            label: "Consignes en cas d'accident",
            status: 'pending',
            pdf: consignesAccident,
            hasSeenAllPages: false
        }
    ]);

    const navigate = useNavigate();
    const { handleAddPassedModule, worksiteId } = useHomeSecurity();
    const [getPpspsByWorksiteId, { data: apiPpsps, isSuccess: isSuccessPpsps }] = useGetPpspsByWorksiteIdMutation();

    useEffect(() => {
        if (!worksiteId) {
            navigate('/sante-securite/home-security');
        }
    }, [worksiteId, navigate]);

    const computedStepperSteps = useMemo((): MinimalStepperProps['steps'] => {
        return steps.map((item, index) => {
            const computedStatus = index < currentStep ? 'completed' : index === currentStep ? 'inProgress' : 'pending';
            return {
                label: item.label,
                status: computedStatus
            };
        });
    }, [steps, currentStep]);

    const canGoBack = useMemo(() => {
        return currentStep > 0;
    }, [currentStep]);

    const handleNext = () => {
        if (currentStep === steps.length - 1) {
            navigate('/sante-securite/home-security/menu');
            handleAddPassedModule('tools');
            return;
        }
        setCurrentStep(currentStep + 1);
    };

    const handlePrev = () => {
        setCurrentStep(currentStep - 1);
    };

    const handleSetHasSeenAllPages = (hasSeenAllPages: boolean) => {
        setSteps((prevSteps) => {
            const currentStepItem = prevSteps[currentStep];

            if (currentStepItem.hasSeenAllPages === hasSeenAllPages) {
                return prevSteps;
            }

            return prevSteps.map((step, index) => (index === currentStep ? { ...step, hasSeenAllPages } : step));
        });
    };

    useEffect(() => {
        if (isChecked) {
            setSteps((prevSteps) => {
                return prevSteps.map((step) => {
                    if (step.label === 'Le PPSPS') {
                        return { ...step, hasSeenAllPages: true };
                    }
                    return { ...step, hasSeenAllPages: true };
                });
            });
        }
    }, [isChecked]);

    useEffect(() => {
        if (isSuccessPpsps && apiPpsps) {
            setPpsps(apiPpsps);
        }
    }, [isSuccessPpsps, apiPpsps]);

    const handleOpenPpspsModal = () => {
        if (worksiteId) {
            getPpspsByWorksiteId({ worksiteId: Number(worksiteId) });
            setIsPpspsModalOpen(true);
        }
    };

    const handleClosePpspsModal = () => {
        setIsPpspsModalOpen(false);
    };

    return {
        computedStepperSteps,
        handleNext,
        handlePrev,
        canGoBack,
        steps,
        currentStep,
        handleSetHasSeenAllPages,
        isChecked,
        setIsChecked,
        isPpspsModalOpen,
        handleOpenPpspsModal,
        handleClosePpspsModal,
        ppsps
    };
};
